import * as React from 'react';

import usePagination, { UsePaginationItem } from '@mui/material/usePagination';
import { styled } from '@mui/material/styles';
import { Box, PaginationItem, PaginationItemOwnProps } from '@mui/material';

import useWindowSize from 'global-components/screenSize/useWindowSize';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
});

interface Props {
  currentPage: number;
  setCurrentPage: (pageNum: number) => void;
  totalPages: number;
}
export default function OhsRegisterTableServerPagination(props: Props) {
  const { currentPage, setCurrentPage, totalPages } = props;
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 700;
  const commonPageItemProps: Partial<PaginationItemOwnProps> = {
    variant: 'outlined',
    shape: 'rounded',
    sx: { background: 'white' },
    size: isMobile ? 'small' : 'medium',
  };

  const { items } = usePagination({
    count: totalPages,
    page: currentPage,
    onChange: (event: React.ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
    },
    boundaryCount: 0,
  });

  const checkIsDisabled = (pageItem: Partial<UsePaginationItem>) => {
    const page = pageItem.page ?? 0;
    let isDisabled = false;

    if (page >= 10 && page !== currentPage && currentPage + 1 !== page) isDisabled = true;
    if (currentPage + 1 < page) isDisabled = true;
    if (page + 1 === currentPage && !(page <= 10)) isDisabled = true;
    if (page <= 10) isDisabled = false;
    if (pageItem.type === 'previous' && currentPage === 1) isDisabled = true;
    if (pageItem.type === 'next' && currentPage === totalPages) isDisabled = true;

    return isDisabled;
  };

  if (!totalPages || typeof totalPages !== 'number' || totalPages <= 1) return null;

  const renderPageItem = (pageItem: Partial<UsePaginationItem>, index: number | string) => {
    return (
      <PaginationItem
        key={index}
        {...pageItem}
        disabled={checkIsDisabled(pageItem)}
        {...commonPageItemProps}
      />
    );
  };

  const renderNavButton = (type: 'previous' | 'next') => {
    const target = items.find((item) => item.type === type);
    if (target) return renderPageItem(target, type);
    return '';
  };

  // render buttons for 1-10
  const renderFixedInitialPages = () => {
    let pageButtons: JSX.Element[] = [];
    const limitCount = isMobile ? 1 : 10;
    const limit = totalPages > limitCount ? limitCount : totalPages;

    for (let counter = 1; counter <= limit; counter += 1) {
      const newPageButton = (
        <PaginationItem
          onClick={() => setCurrentPage(counter)}
          selected={counter === currentPage}
          page={counter}
          {...commonPageItemProps}
        />
      );
      pageButtons = [...pageButtons, newPageButton];
    }
    return pageButtons;
  };

  // render buttons 11 and above
  const renderConditionalPageButtons = () => {
    return items.map((pageItem, index) => {
      if (
        pageItem.type === 'next' ||
        pageItem.type === 'previous' ||
        (typeof pageItem.page === 'number' && pageItem.page < (isMobile ? 2 : 11)) ||
        (pageItem.type === 'start-ellipsis' && currentPage < (isMobile ? 3 : 13)) ||
        pageItem.page === totalPages
      )
        return '';
      return renderPageItem(pageItem, index);
    });
  };

  const renderLastPageButton = () => {
    const limitCount = isMobile ? 1 : 10;

    // Don't render if already included in the initial set
    if (totalPages <= limitCount) return null;

    return renderPageItem(
      {
        onClick: () => setCurrentPage(totalPages),
        selected: currentPage === totalPages,
        page: totalPages,
      },
      totalPages
    );
  };

  return (
    <Box mt={2} mx={screenSize.width < 360 ? -1 : 0}>
      <List>
        {renderNavButton('previous')}
        {renderFixedInitialPages()}
        {renderConditionalPageButtons()}
        {renderLastPageButton()}
        {renderNavButton('next')}
      </List>
    </Box>
  );
}
